import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Col, Container, Row, Card, CardBody, Button, Spinner } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import axios from "axios"
import { baseUrl } from "../../Network"
import { handleAuthError, openExternalLaunchUrl } from "../../utils"
import { toast } from "react-toastify"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import { LayoutDashboard, Users, Eye, EyeOff, Sparkles } from "lucide-react"
import { isMarketerReadOnly } from "../../helpers/adminRole"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const ManageAiDashboard = () => {
  document.title = "AI | RYD Admin"
  const readOnly = isMarketerReadOnly()
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exploringAi, setExploringAi] = useState(false)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const res = await axios.get(`${baseUrl}/admin/subscription/ai-summary`)
        if (mounted && res?.data?.status) {
          setSummary(res.data.data)
        }
      } catch (e) {
        handleAuthError(e)
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  const handleExploreAiTutor = async () => {
    const popup = window.open("about:blank", "_blank")
    try {
      setExploringAi(true)
      const res = await axios.post(`${baseUrl}/admin/ai/explore`, {})
      if (res?.data?.status && res?.data?.data?.url) {
        if (popup) {
          try {
            popup.opener = null
          } catch (_) {
            /* ignore */
          }
          popup.location.href = res.data.data.url
        } else {
          openExternalLaunchUrl(res.data.data.url)
        }
        toast.success("Opening AI Tutor...")
        return
      }
      popup?.close()
      toast.error(res?.data?.message || "Unable to open AI Tutor")
    } catch (e) {
      popup?.close()
      handleAuthError(e)
      toast.error("Unable to open AI Tutor")
    } finally {
      setExploringAi(false)
    }
  }

  const metrics = useMemo(() => {
    const visiblePlans = Number(summary?.visiblePlans || 0)
    const hiddenPlans = Number(summary?.hiddenPlans || 0)
    const totalPlans = Number(summary?.totalPlans ?? (visiblePlans + hiddenPlans))
    const totalParents = Number(summary?.totalParents || 0)
    const activeParents = Number(summary?.activeParents || 0)
    const activeRows = Number(summary?.activeSubscriptionRows || 0)
    return {
      visiblePlans,
      hiddenPlans,
      totalParents,
      activeParents,
      activeRows,
      totalPlans,
    }
  }, [summary])

  const chartData = useMemo(() => {
    if (!summary) return null
    return {
      labels: ["Total plans", "Visible plans", "Total parents", "Active AI subscribers"],
      datasets: [
        {
          label: "Counts",
          data: [
            metrics.totalPlans,
            metrics.visiblePlans,
            metrics.totalParents,
            metrics.activeParents,
          ],
          backgroundColor: [
            "rgba(59, 130, 246, 0.45)",
            "rgba(136, 22, 122, 0.55)",
            "rgba(16, 185, 129, 0.55)",
            "rgba(234, 179, 8, 0.55)",
          ],
          borderRadius: 8,
        },
      ],
    }
  }, [summary, metrics])

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: false },
      },
      scales: {
        y: { beginAtZero: true, ticks: { stepSize: 1 } },
      },
    }),
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="align-items-center mb-3">
            <Col md={6}>
              <Breadcrumbs title="AI" breadcrumbItem="Dashboard" />
            </Col>
            <Col md={6} className="text-md-end">
              <Button
                color="info"
                className="me-2"
                onClick={handleExploreAiTutor}
                disabled={exploringAi}
              >
                {exploringAi ? "Opening AI Tutor..." : "Explore AI Tutor"}
              </Button>
              <Button tag={Link} to="/dashboard" color="light">
                <LayoutDashboard className="me-1" size={16} style={{ verticalAlign: "text-top" }} />
                {readOnly ? "Main dashboard" : "Back to main dashboard"}
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <Row className="g-3 mb-4">
                <Col md={3} sm={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{ width: 48, height: 48, background: "rgba(59, 130, 246, 0.12)" }}
                      >
                        <Sparkles className="text-primary" size={22} />
                      </div>
                      <div>
                        <div className="text-muted small">Total plans</div>
                        <div className="h4 mb-0">{metrics.totalPlans}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3} sm={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{ width: 48, height: 48, background: "rgba(136, 22, 122, 0.1)" }}
                      >
                        <Eye className="text-primary" size={22} />
                      </div>
                      <div>
                        <div className="text-muted small">Visible plans</div>
                        <div className="h4 mb-0">{metrics.visiblePlans}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3} sm={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{ width: 48, height: 48, background: "rgba(16, 185, 129, 0.12)" }}
                      >
                        <Users className="text-success" size={22} />
                      </div>
                      <div>
                        <div className="text-muted small">Total AI parents</div>
                        <div className="h4 mb-0">{metrics.totalParents}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3} sm={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{ width: 48, height: 48, background: "rgba(234, 179, 8, 0.16)" }}
                      >
                        <EyeOff className="text-warning" size={22} />
                      </div>
                      <div>
                        <div className="text-muted small">Active AI subscribers</div>
                        <div className="h4 mb-0">{metrics.activeParents}</div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Row className="mt-1">
                <Col lg={12}>
                  <Card className="border-0 shadow-sm">
                    <CardBody>
                      <h5 className="card-title mb-3">AI metrics chart</h5>
                      {chartData && (
                        <div style={{ maxHeight: 300 }}>
                          <Bar data={chartData} options={chartOptions} />
                        </div>
                      )}
                      <div className="mt-3 small text-muted">
                        <Link to="/ai/parents">AI parents list</Link> {" · "}
                        <Link to="/ai/subscribers">Subscribed parents</Link> {" · "}
                        <Link to="/ai/subscription-plans">Subscription plans</Link>
                        {!readOnly && (
                        <div className="mt-2">
                          To cancel a parent&apos;s AI subscription, open the list and use the row <strong>Actions</strong> menu (cancel at period end or immediately).
                        </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withAuth(ManageAiDashboard)
